import React, { useState } from "react";
import {
    Modal,
    Upload,
    Button,
    Steps,
    Card,
    Typography,
    Table,
    Tag,
    Alert,
    Descriptions,
    Space,
    Divider,
    Progress,
    message,
    Tooltip,
    Badge,
    Collapse
} from "antd";
import {
    InboxOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    FileExcelOutlined,
    RocketOutlined,
    ArrowLeftOutlined,
    CodeOutlined,
    FunctionOutlined,
    FileTextOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { PostJutgeActivitatIdProblema } from "../../../../dao/jutge/PostJutgeActivitatIdProblema";
import { PostJutgeProblemaIdTest } from "../../../../dao/jutge/PostJutgeProblemaIdTest";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface Props {
    visible: boolean;
    id_activitat: string;
    onClose: () => void;
    onSuccess: () => void;
}

export interface ParsedProblemaGeneral {
    nom: string;
    descripcio: string;
    dificultat: string; // beginner | intermediate | expert
    tags: string;
    multi: boolean;
    puntuacion: number;
    inici?: string | null;
    final?: string | null;
    entrada?: string;
    sortida?: string;
}

export interface ParsedProblemaTest {
    name: string;
    tipus: "es" | "method" | "methodtest" | "content" | "io_file" | "db";
    entrada?: string;
    sortida?: string;
    methodName?: string;
    returnType?: string;
    modifiers?: string[];
    parameters?: Array<{ paramName: string; paramType: string }>;
    contentText?: string;
    mustContain?: boolean;
}

export const ImportarExerciciExcelModal: React.FC<Props> = ({
    visible,
    id_activitat,
    onClose,
    onSuccess
}) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [parsedGeneral, setParsedGeneral] = useState<ParsedProblemaGeneral | null>(null);
    const [parsedTests, setParsedTests] = useState<ParsedProblemaTest[]>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitProgress, setSubmitProgress] = useState<number>(0);
    const [statusMessage, setStatusMessage] = useState<string>("");

    const resetState = () => {
        setCurrentStep(0);
        setParsedGeneral(null);
        setParsedTests([]);
        setValidationErrors([]);
        setValidationWarnings([]);
        setIsSubmitting(false);
        setSubmitProgress(0);
        setStatusMessage("");
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetState();
            onClose();
        }
    };

    // 1. Download Template generator
    const handleDownloadTemplate = () => {
        try {
            const wb = XLSX.utils.book_new();

            // Sheet 1: General
            const generalData = [
                ["Camp", "Valor", "Descripcio / Opcions"],
                ["Nom", "Suma de dos nombres", "Títol de l'exercici (Obligatori)"],
                ["Descripcio", "Escriu un programa que llegeixi dos nombres enters i en mostri la suma.", "Descripció detallada del problema (accepta Markdown)"],
                ["Dificultat", "beginner", "beginner | intermediate | expert"],
                ["Tags", "#intro #bucles #e/s", "Etiquetes separades per espai (ex: #print #funcions)"],
                ["Multi_File", "NO", "SI si és multifitxer, NO si és un sol arxiu"],
                ["Puntuacio", 1, "Puntuació de prestigi de l'exercici (per defecte 1)"],
                ["Inici", "2026-10-01 08:00:00", "Data d'obertura YYYY-MM-DD HH:mm:ss (opcional)"],
                ["Final", "2026-10-31 23:59:59", "Data límit d'entrega YYYY-MM-DD HH:mm:ss (opcional)"],
                ["Entrada_Exemple", "3 4", "Exemple d'entrada mostrat a l'enunciat"],
                ["Sortida_Exemple", "7", "Exemple de sortida esperada per a l'entrada anterior"]
            ];
            const wsGeneral = XLSX.utils.aoa_to_sheet(generalData);
            wsGeneral["!cols"] = [{ wch: 18 }, { wch: 45 }, { wch: 55 }];
            XLSX.utils.book_append_sheet(wb, wsGeneral, "General");

            // Sheet 2: Tests
            const testsData = [
                [
                    "Nom_Test",
                    "Tipus",
                    "Entrada",
                    "Sortida",
                    "Nom_Metode",
                    "Tipus_Retorn",
                    "Modificadors",
                    "Parametres",
                    "Contingut_Codi",
                    "Ha_De_Contenir"
                ],
                [
                    "Test 1 - Valors Positius",
                    "es",
                    "5 10\n",
                    "15\n",
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                ],
                [
                    "Test 2 - Valors Negatius",
                    "es",
                    "-3 7\n",
                    "4\n",
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                ],
                [
                    "Test 3 - Mètode sumar",
                    "method",
                    "",
                    "",
                    "sumar",
                    "int",
                    "public, static",
                    "int a, int b",
                    "",
                    ""
                ],
                [
                    "Test 4 - Ús de Scanner",
                    "content",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "Scanner",
                    "SI"
                ]
            ];
            const wsTests = XLSX.utils.aoa_to_sheet(testsData);
            wsTests["!cols"] = [
                { wch: 25 },
                { wch: 12 },
                { wch: 20 },
                { wch: 20 },
                { wch: 18 },
                { wch: 14 },
                { wch: 18 },
                { wch: 18 },
                { wch: 20 },
                { wch: 16 }
            ];
            XLSX.utils.book_append_sheet(wb, wsTests, "Tests");

            // Sheet 3: Instruccions d'ajuda
            const ajudaData = [
                ["Guia de Format Excel per Crear Exercicis"],
                [""],
                ["1. Full 'General': Especifica les dades de l'exercici. No canviïs els noms de la columna 'Camp'."],
                ["2. Full 'Tests': Defineix els casos de prova que s'avaluaran automàticament."],
                ["   - Tipus 'es': Prova d'Entrada/Sortida estàndard (requereix 'Entrada' i 'Sortida')."],
                ["   - Tipus 'method': Prova de signatura de mètode (requereix 'Nom_Metode' i 'Tipus_Retorn')."],
                ["   - Tipus 'content': Comprovació d'estil o presència de text al codi ('Contingut_Codi' i 'Ha_De_Contenir' SI/NO)."],
                ["3. Seguretat en la càrrega: Si qualsevol test falla en registrar-se, l'operació s'atura per no deixar dades corruptes."]
            ];
            const wsAyuda = XLSX.utils.aoa_to_sheet(ajudaData);
            XLSX.utils.book_append_sheet(wb, wsAyuda, "Instruccions_Ajuda");

            XLSX.writeFile(wb, "Plantilla_Nou_Exercici.xlsx");
            message.success("Plantilla descarregada correctament");
        } catch (error) {
            console.error("Error en generar plantilla:", error);
            message.error("Error en descarregar la plantilla Excel");
        }
    };

    // 2. Parse uploaded file
    const handleFileUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const buffer = e.target?.result;
                const wb = XLSX.read(buffer, { type: "array" });

                const errors: string[] = [];
                const warnings: string[] = [];

                if (!wb.SheetNames.includes("General")) {
                    errors.push("Falta el full obligatori 'General'. Utilitza la plantilla oficial.");
                }
                if (!wb.SheetNames.includes("Tests")) {
                    errors.push("Falta el full obligatori 'Tests'. Utilitza la plantilla oficial.");
                }

                if (errors.length > 0) {
                    setValidationErrors(errors);
                    setCurrentStep(1);
                    return;
                }

                // Parse General Sheet
                const wsGen = wb.Sheets["General"];
                const genRows: any[][] = XLSX.utils.sheet_to_json(wsGen, { header: 1 });
                const genMap: Record<string, any> = {};

                for (let i = 1; i < genRows.length; i++) {
                    const row = genRows[i];
                    if (row && row[0] !== undefined) {
                        const key = String(row[0]).trim().toLowerCase();
                        genMap[key] = row[1];
                    }
                }

                const nom = String(genMap["nom"] || genMap["nombre"] || "").trim();
                const descripcio = String(genMap["descripcio"] || genMap["descripcion"] || "").trim();
                const dificultatRaw = String(genMap["dificultat"] || genMap["dificultad"] || "beginner").trim().toLowerCase();
                const tags = String(genMap["tags"] || "#programacio").trim();
                const multiRaw = String(genMap["multi_file"] || genMap["multi"] || "NO").trim().toUpperCase();
                const puntuacionRaw = genMap["puntuacio"] || genMap["puntuacion"];
                const inici = genMap["inici"] || genMap["inicio"] ? String(genMap["inici"] || genMap["inicio"]).trim() : null;
                const final = genMap["final"] || genMap["fin"] ? String(genMap["final"] || genMap["fin"]).trim() : null;
                const entrada = genMap["entrada_exemple"] !== undefined ? String(genMap["entrada_exemple"]) : "";
                const sortida = genMap["sortida_exemple"] !== undefined ? String(genMap["sortida_exemple"]) : "";

                if (!nom) {
                    errors.push("El camp 'Nom' és obligatori al full General.");
                }
                if (!descripcio) {
                    warnings.push("La descripció està buida. Es recomana afegir un enunciat.");
                }

                let dificultat = "beginner";
                if (["beginner", "intermediate", "expert"].includes(dificultatRaw)) {
                    dificultat = dificultatRaw;
                } else {
                    warnings.push(`Dificultat '${dificultatRaw}' no reconeguda. S'usarà 'beginner'.`);
                }

                const multi = multiRaw === "SI" || multiRaw === "TRUE" || multiRaw === "1" || multiRaw === "YES";
                const puntuacion = Number(puntuacionRaw) > 0 ? Number(puntuacionRaw) : 1;

                const parsedGen: ParsedProblemaGeneral = {
                    nom,
                    descripcio,
                    dificultat,
                    tags,
                    multi,
                    puntuacion,
                    inici,
                    final,
                    entrada,
                    sortida
                };

                // Parse Tests Sheet
                const wsTests = wb.Sheets["Tests"];
                const testRows: any[][] = XLSX.utils.sheet_to_json(wsTests, { header: 1 });
                const tests: ParsedProblemaTest[] = [];

                for (let i = 1; i < testRows.length; i++) {
                    const row = testRows[i];
                    if (!row || row.length === 0 || !row[0]) continue;

                    const testName = String(row[0] || `Test ${i}`).trim();
                    const tipusRaw = String(row[1] || "es").trim().toLowerCase();
                    const entradaTest = row[2] !== undefined ? String(row[2]) : "";
                    const sortidaTest = row[3] !== undefined ? String(row[3]) : "";
                    const methodName = row[4] !== undefined ? String(row[4]).trim() : "";
                    const returnType = row[5] !== undefined ? String(row[5]).trim() : "void";
                    const modifiersRaw = row[6] !== undefined ? String(row[6]) : "";
                    const paramsRaw = row[7] !== undefined ? String(row[7]) : "";
                    const contentText = row[8] !== undefined ? String(row[8]) : "";
                    const mustContainRaw = row[9] !== undefined ? String(row[9]).trim().toUpperCase() : "SI";

                    const modifiers = modifiersRaw
                        ? modifiersRaw.split(",").map((s) => s.trim()).filter(Boolean)
                        : ["public", "static"];

                    const parameters: Array<{ paramName: string; paramType: string }> = [];
                    if (paramsRaw) {
                        const splitted = paramsRaw.split(",");
                        for (const p of splitted) {
                            const trimmed = p.trim();
                            if (trimmed) {
                                const tokens = trimmed.split(/\s+/);
                                if (tokens.length >= 2) {
                                    parameters.push({ paramType: tokens[0], paramName: tokens[1] });
                                } else {
                                    parameters.push({ paramType: tokens[0], paramName: `p${parameters.length + 1}` });
                                }
                            }
                        }
                    }

                    let tipus: "es" | "method" | "methodtest" | "content" | "io_file" | "db" = "es";
                    if (["es", "method", "methodtest", "content", "io_file", "db"].includes(tipusRaw)) {
                        tipus = tipusRaw as any;
                    }

                    if ((tipus === "es" || tipus === "methodtest") && !sortidaTest && !entradaTest) {
                        warnings.push(`Fila de test ${i} (${testName}): no té ni entrada ni sortida especificada.`);
                    }

                    tests.push({
                        name: testName,
                        tipus,
                        entrada: entradaTest,
                        sortida: sortidaTest,
                        methodName,
                        returnType,
                        modifiers,
                        parameters,
                        contentText,
                        mustContain: mustContainRaw === "SI" || mustContainRaw === "TRUE" || mustContainRaw === "1"
                    });
                }

                if (tests.length === 0) {
                    warnings.push("No s'ha definit cap test al full 'Tests'. L'exercici no tindrà avaluació automàtica inicial.");
                }

                setParsedGeneral(parsedGen);
                setParsedTests(tests);
                setValidationErrors(errors);
                setValidationWarnings(warnings);
                setCurrentStep(1);
            } catch (err: any) {
                console.error("Error processant fitxer Excel:", err);
                message.error("Error al processar el fitxer Excel. Revisa el format.");
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // 3. Confirm & execute import
    const handleConfirmImport = async () => {
        if (!parsedGeneral) {
            message.error("Dades insuficients per crear l'exercici");
            return;
        }

        setIsSubmitting(true);
        setSubmitProgress(10);
        setStatusMessage("Creant exercici a la base de dades...");

        try {
            const targetAct = id_activitat && id_activitat !== "undefined" ? id_activitat : "0";

            const payload: any = {
                id: 0,
                nom: parsedGeneral.nom,
                descripcio: parsedGeneral.descripcio,
                dificultat: parsedGeneral.dificultat,
                tags: parsedGeneral.tags,
                multi: parsedGeneral.multi ? 1 : 0,
                puntuacion: parsedGeneral.puntuacion,
                entrada: parsedGeneral.entrada || "",
                sortida: parsedGeneral.sortida || "",
                inici: parsedGeneral.inici || null,
                fin: parsedGeneral.final || null
            };

            const createdRes: any = await PostJutgeActivitatIdProblema(targetAct, payload);

            let createdId: number | null = null;
            if (createdRes && typeof createdRes.id === "number") {
                createdId = createdRes.id;
            } else if (createdRes && typeof createdRes.id === "string") {
                createdId = parseInt(createdRes.id, 10);
            } else if (typeof createdRes === "number") {
                createdId = createdRes;
            }

            if (!createdId) {
                throw new Error("No s'ha pogut obtenir l'ID de l'exercici creat. Operació cancel·lada.");
            }

            setSubmitProgress(35);
            setStatusMessage(`Exercici creat (ID #${createdId}). Afegint els tests associats...`);

            // If we have tests, create them one by one.
            // If any test fails, STOP and notify user to maintain consistency.
            if (parsedTests.length > 0) {
                const totalTests = parsedTests.length;
                let processedTests = 0;

                for (const t of parsedTests) {
                    let testPayload: any = {
                        name: t.name,
                        mode: t.tipus,
                        tipus: t.tipus
                    };

                    if (t.tipus === "es") {
                        testPayload = {
                            ...testPayload,
                            entrada: t.entrada || "",
                            sortida: t.sortida || ""
                        };
                    } else if (t.tipus === "methodtest") {
                        testPayload = {
                            ...testPayload,
                            entrada: t.entrada || "",
                            sortida: t.sortida || "",
                            methodName: t.methodName || "metode"
                        };
                    } else if (t.tipus === "method") {
                        testPayload = {
                            ...testPayload,
                            methodName: t.methodName || "metode",
                            modifiers: t.modifiers || ["public", "static"],
                            returnType: t.returnType || "void",
                            parameters: t.parameters || [],
                            fitxer: JSON.stringify({
                                methodName: t.methodName || "metode",
                                modifiers: t.modifiers || ["public", "static"],
                                returnType: t.returnType || "void",
                                parameters: t.parameters || []
                            })
                        };
                    } else if (t.tipus === "content") {
                        testPayload = {
                            ...testPayload,
                            searchText: t.contentText || "",
                            mustContain: t.mustContain !== undefined ? t.mustContain : true,
                            fitxer: JSON.stringify({
                                text: t.contentText || "",
                                must: t.mustContain !== undefined ? t.mustContain : true
                            })
                        };
                    }

                    try {
                        await PostJutgeProblemaIdTest(String(createdId), testPayload);
                    } catch (testErr: any) {
                        throw new Error(`Fallada en registrar el test '${t.name}': ${testErr?.message || testErr}. S'ha interromput la càrrega.`);
                    }

                    processedTests++;
                    const progress = 35 + Math.round((processedTests / totalTests) * 60);
                    setSubmitProgress(progress);
                    setStatusMessage(`Creant test ${processedTests} de ${totalTests}...`);
                }
            }

            setSubmitProgress(100);
            setStatusMessage("Exercici i tests creats amb èxit!");
            message.success("Exercici importat correctament des d'Excel!");

            setTimeout(() => {
                resetState();
                onSuccess();
                onClose();
            }, 600);
        } catch (error: any) {
            console.error("Error important exercici:", error);
            message.error(`Error durant la importació: ${error?.message || "Error desconegut"}`);
            setIsSubmitting(false);
        }
    };

    const testColumns = [
        {
            title: "Nom",
            dataIndex: "name",
            key: "name",
            width: 220
        },
        {
            title: "Tipus",
            dataIndex: "tipus",
            key: "tipus",
            width: 100,
            render: (tipus: string) => {
                const color = tipus === "es" ? "blue" : tipus === "method" ? "purple" : tipus === "methodtest" ? "geekblue" : "orange";
                return <Tag color={color}>{tipus.toUpperCase()}</Tag>;
            }
        },
        {
            title: "Detall",
            key: "detall",
            render: (_: any, r: ParsedProblemaTest) => {
                if (r.tipus === "es") {
                    return (
                        <span style={{ fontSize: 12 }}>
                            <b>In:</b> {r.entrada ? r.entrada.replace(/\n/g, "↵ ") : "—"} | <b>Out:</b> {r.sortida ? r.sortida.replace(/\n/g, "↵ ") : "—"}
                        </span>
                    );
                }
                if (r.tipus === "methodtest") {
                    return (
                        <span style={{ fontSize: 12 }}>
                            {r.methodName ? (
                                <Tag color="purple" style={{ marginRight: 6 }}>
                                    <code>{r.methodName}()</code>
                                </Tag>
                            ) : null}
                            <b>In:</b> {r.entrada ? r.entrada.replace(/\n/g, "↵ ") : "—"} | <b>Out:</b> {r.sortida ? r.sortida.replace(/\n/g, "↵ ") : "—"}
                        </span>
                    );
                }
                if (r.tipus === "method") {
                    return (
                        <span style={{ fontSize: 12 }}>
                            <code>{r.modifiers?.join(" ")} {r.returnType} {r.methodName}({r.parameters?.map(p => `${p.paramType} ${p.paramName}`).join(", ")})</code>
                        </span>
                    );
                }
                if (r.tipus === "content") {
                    return (
                        <span style={{ fontSize: 12 }}>
                            {r.mustContain ? "Ha de contenir: " : "Prohibit: "} <code>{r.contentText}</code>
                        </span>
                    );
                }
                return "-";
            }
        }
    ];

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            width={850}
            title={
                <Space>
                    <FileExcelOutlined style={{ color: "#217346", fontSize: 20 }} />
                    <span>Importar Nou Exercici des d'Excel</span>
                </Space>
            }
            footer={null}
            destroyOnClose
        >
            <div style={{ padding: "8px 0" }}>
                <Steps
                    current={currentStep}
                    size="small"
                    style={{ marginBottom: 20 }}
                    items={[
                        { title: "Descarregar / Pujar Excel" },
                        { title: "Previsualització i Validació" },
                        { title: "Importar Exercici" }
                    ]}
                />

                {/* STEP 0: Upload & Template */}
                {currentStep === 0 && (
                    <div>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Creació ràpida mitjançant plantilla oficial"
                            description="Pots descarregar la plantilla Excel amb l'estructura requerida, omplir les dades de l'exercici i els seus tests, i pujar-la aquí per importar-lo automàticament."
                        />

                        <div style={{ textAlign: "center", marginBottom: 20 }}>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadTemplate}
                                type="dashed"
                                size="large"
                                style={{ borderColor: "#217346", color: "#217346" }}
                            >
                                Descarregar Plantilla Excel (.xlsx)
                            </Button>
                        </div>

                        <Dragger
                            accept=".xlsx, .xls"
                            showUploadList={false}
                            beforeUpload={(file) => {
                                handleFileUpload(file);
                                return false;
                            }}
                            style={{ padding: "30px 10px" }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: "#217346", fontSize: 48 }} />
                            </p>
                            <p className="ant-upload-text">Arrossega el fitxer Excel omplert aquí, o fes clic per seleccionar-lo</p>
                            <p className="ant-upload-hint">Format suportat: fitxer .xlsx generat a partir de la plantilla</p>
                        </Dragger>
                    </div>
                )}

                {/* STEP 1: Preview & Validation */}
                {currentStep === 1 && (
                    <div>
                        {validationErrors.length > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="S'han detectat errors al fitxer:"
                                description={
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {validationErrors.map((err, i) => (
                                            <li key={i}>{err}</li>
                                        ))}
                                    </ul>
                                }
                            />
                        )}

                        {validationWarnings.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="Avisos sobre el contingut:"
                                description={
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {validationWarnings.map((warn, i) => (
                                            <li key={i}>{warn}</li>
                                        ))}
                                    </ul>
                                }
                            />
                        )}

                        {parsedGeneral && (
                            <Card size="small" title="Informació de l'Exercici" style={{ marginBottom: 16 }}>
                                <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                                    <Descriptions.Item label="Nom">{parsedGeneral.nom}</Descriptions.Item>
                                    <Descriptions.Item label="Dificultat">
                                        <Tag color={parsedGeneral.dificultat === "beginner" ? "green" : parsedGeneral.dificultat === "intermediate" ? "orange" : "red"}>
                                            {parsedGeneral.dificultat.toUpperCase()}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Multifitxer">
                                        {parsedGeneral.multi ? <Tag color="purple">SÍ</Tag> : <Tag>NO</Tag>}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Etiquetes">{parsedGeneral.tags}</Descriptions.Item>
                                    <Descriptions.Item label="Punts">{parsedGeneral.puntuacion}</Descriptions.Item>
                                    <Descriptions.Item label="Dates">
                                        {parsedGeneral.inici || parsedGeneral.final ? (
                                            <span>{parsedGeneral.inici || "Sense inici"} ➔ {parsedGeneral.final || "Sense final"}</span>
                                        ) : "Sense restricció"}
                                    </Descriptions.Item>
                                </Descriptions>
                                {parsedGeneral.descripcio && (
                                    <div style={{ marginTop: 8 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Descripció: </Text>
                                        <Text style={{ fontSize: 12 }}>{parsedGeneral.descripcio}</Text>
                                    </div>
                                )}
                            </Card>
                        )}

                        <Card size="small" title={`Tests Unitaris (${parsedTests.length})`} style={{ marginBottom: 16 }}>
                            <Table
                                dataSource={parsedTests}
                                columns={testColumns}
                                rowKey="name"
                                size="small"
                                pagination={false}
                                scroll={{ y: 220 }}
                            />
                        </Card>

                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                            <Button icon={<ArrowLeftOutlined />} onClick={() => setCurrentStep(0)} disabled={isSubmitting}>
                                Tornar
                            </Button>
                            <Button
                                type="primary"
                                icon={<RocketOutlined />}
                                disabled={validationErrors.length > 0}
                                loading={isSubmitting}
                                onClick={handleConfirmImport}
                                style={{ backgroundColor: "#217346", borderColor: "#217346" }}
                            >
                                Confirmar i Crear Exercici
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 2 / In Progress Display */}
                {isSubmitting && (
                    <div style={{ textAlign: "center", padding: "30px 20px" }}>
                        <Progress percent={submitProgress} status="active" strokeColor="#217346" />
                        <p style={{ marginTop: 16, fontWeight: 600, color: "#217346" }}>{statusMessage}</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ImportarExerciciExcelModal;
